import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const areas = [
  ["Leadership Development", "Growing leaders who lead themselves first — with responsibility, intentionality and influence."],
  ["People Development", "Practical growth journeys for the humans behind your projects, teams and outcomes."],
  ["Coaching", "One-to-one and group coaching that is safe but not passive — honest, warm and useful."],
  ["Facilitation", "Rooms where people actually talk honestly. Retreats, team days and guided conversations."],
  ["Training", "HRDF-certified training in communication, connection, resilience and personal leadership."],
  ["Team Development", "Trust, repair and healthy dynamics — because culture is built one relationship at a time."],
  ["Resilience", "Helping teams navigate disruption, change and uncertainty without losing themselves."],
  ["Communication & Connection", "Everyone communicates, few connect. We close that gap."],
  ["Culture & People Growth", "Environments where people feel safe enough to grow — and believed in enough to try."],
];

const multipliers = ["One person grows", "Their family feels it", "Their team changes", "The organisation shifts", "Generations inherit it"];

export default function Organisations() {
  useEffect(() => {
    document.title = "For Organisations — People Growing People";
  }, []);

  return (
    <main data-testid="organisations-page">
      {/* HERO — dark with stage photo */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden bg-ink">
        <img
          src="/alex-speaking.jpg"
          alt="Alex speaking on stage with open hands"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-48 w-full">
          <MaskedLine delay={0.15}>
            <span className="text-xs uppercase tracking-[0.3em] text-paper/60">For Organisations</span>
          </MaskedLine>
          <h1 className="font-serif text-paper text-[10vw] sm:text-6xl md:text-8xl leading-[0.95] tracking-tight mt-6 max-w-5xl">
            <MaskedLine delay={0.3}>Projects don't grow</MaskedLine>
            <MaskedLine delay={0.42}>organisations.</MaskedLine>
            <MaskedLine delay={0.54}>
              <span className="italic text-clay">Growing people do.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-8 max-w-2xl">
            <span className="text-paper/80 text-base md:text-lg leading-relaxed">
              Behind every project, metric and culture deck are people. When your
              people grow, everything they touch grows with them.
            </span>
          </MaskedLine>
          <MaskedLine delay={0.85} className="mt-10">
            <Link
              to="/contact"
              data-testid="org-hero-cta"
              className="group inline-flex items-center gap-3 bg-clay text-paper text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Let's Talk About Your People
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </MaskedLine>
        </div>
      </section>

      {/* THE MULTIPLIER */}
      <section className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <Overline>The Multiplication</Overline>
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-6">
                  Grow one person.{" "}
                  <span className="italic text-moss">Watch it ripple.</span>
                </h2>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-7 md:pl-12 md:border-l md:border-line">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                Organisational work with Alex is not another corporate programme
                with lanyards and flipcharts. It is the same deeply human work —
                adapted for your context, your people and your season.
              </p>
            </Reveal>
            <div className="mt-12 border-t border-line">
              {multipliers.map((m, i) => (
                <Reveal key={m} delay={i * 0.06}>
                  <div data-testid={`multiplier-${i + 1}`} className="flex items-baseline gap-6 py-6 border-b border-line">
                    <span className="font-serif text-lg text-clay shrink-0">0{i + 1}</span>
                    <p className="font-serif text-xl md:text-3xl tracking-tight text-ink/85">{m}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section data-testid="org-areas" className="border-b border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>Ways to Engage</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              Practical, human, <span className="italic text-clay">and built around your people.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {areas.map(([t, c], i) => (
              <Reveal key={t} delay={Math.min(i * 0.05, 0.35)} className="bg-paper">
                <div data-testid={`org-area-${i + 1}`} className="p-8 min-h-[190px] flex flex-col group hover:bg-sand/60 transition-colors duration-300">
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight leading-snug group-hover:text-moss transition-colors duration-300">{t}</h3>
                  <p className="text-sm text-ink/65 mt-4 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-12 text-sm md:text-base text-ink/60 max-w-2xl leading-relaxed">
              Alex is a Maxwell Leadership Certified Trainer and HRDF
              Train-the-Trainer certified — but the credentials are not the
              headline. The headline is what happens to your people.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PHOTO + APPROACH */}
      <section className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <p className="font-serif text-3xl md:text-4xl tracking-tight leading-snug">
                Workshops, masterminds, coaching and journeys —{" "}
                <span className="italic text-moss">in the room or online.</span>
              </p>
              <p className="mt-6 text-sm md:text-base text-ink/70 leading-relaxed">
                Every engagement begins with listening: where your people are,
                what the season demands, and what growth would actually look
                like. Then we build the room for it.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex-workshop.jpg"
                  alt="Alex facilitating a room of leaders"
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover"
                  data-testid="org-workshop-photo"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-moss text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[0.98] max-w-4xl">
              Your projects are waiting on{" "}
              <span className="italic">your people.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-paper/80 max-w-xl">
              Tell us about your team, your season and what you're hoping to
              grow. We'll explore what's possible — together.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/contact"
              data-testid="org-cta-contact"
              className="group mt-12 inline-flex items-center gap-3 bg-paper text-ink text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-clay hover:text-paper transition-colors duration-300"
            >
              Begin the Conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
